import {
  AfterViewInit,
  Component,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  input,
  signal,
} from '@angular/core';
import { GoogleMap, GoogleMapsModule, MapAdvancedMarker, MapInfoWindow } from '@angular/google-maps';

import { MarkerClusterer } from '@googlemaps/markerclusterer';

import { FontIconComponent } from '@camelot/icons';
import { ButtonComponent, TextComponent } from '@camelot/ui';
import { CamAbstractComponent } from '@camelot/utils';

@Component({
  selector: 'cam-google-maps',
  imports: [ButtonComponent, FontIconComponent, GoogleMapsModule, TextComponent],
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss',
})
export class MapComponent extends CamAbstractComponent implements OnChanges, AfterViewInit {
  points = input.required<
    {
      lat: number;
      lng: number;
      title: string;
    }[]
  >();

  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  @ViewChild('map') map!: GoogleMap;
  @ViewChildren(MapAdvancedMarker) mapMarkers!: QueryList<MapAdvancedMarker>;

  readonly isSatellite = signal(false);

  options: google.maps.MapOptions = {
    mapId: 'CAMELOT_MAP',
    mapTypeControl: false,
  };

  readonly markers = signal<{ position: google.maps.LatLngLiteral; title: string }[]>([]);

  readonly selectedMarkerTitle = signal<string>('');

  ngOnChanges(changes: SimpleChanges) {
    if (changes['points'] && this.points()) {
      this.markers.set(
        this.points()
          .filter(point => point.lat && point.lng)
          .map(point => ({
            position: {
              lat: point.lat,
              lng: point.lng,
            },
            title: point.title,
          }))
      );
    }
  }

  ngAfterViewInit() {
    this._registerSubscription(
      this.mapMarkers.changes.subscribe(() => {
        this._doCluster();
      })
    );
    this._doCluster();
  }

  public toggleMapType(): void {
    this.isSatellite.update(v => !v);
    const currentCenter = this.map.getCenter();
    const currentZoom = this.map.getZoom();

    this.options = {
      ...this.options,
      center: currentCenter?.toJSON(),
      mapTypeId: this.isSatellite() ? google.maps.MapTypeId.SATELLITE : google.maps.MapTypeId.ROADMAP,
      zoom: currentZoom,
    };
  }

  public openInfoWindow(marker: MapAdvancedMarker) {
    this.selectedMarkerTitle.set(marker.title);
    if (this.infoWindow) {
      this.infoWindow.open(marker);
    }
  }

  private _doCluster() {
    if (!this.mapMarkers || !this.map) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();

    this.mapMarkers.forEach(marker => {
      const pos = marker.advancedMarker?.position;
      if (pos) {
        bounds.extend(pos as google.maps.LatLngLiteral);
      }
    });

    this.map.fitBounds(bounds, { bottom: 50, left: 50, right: 50, top: 50 });

    if (this.mapMarkers.length === 1) {
      google.maps.event.addListenerOnce(this.map.googleMap!, 'idle', () => {
        this.map.googleMap!.setZoom(14);
      });
    }

    new MarkerClusterer({
      map: this.map.googleMap!,
      markers: this.mapMarkers.map(m => m.advancedMarker),
    });
  }
}
